import React from 'react';
import { BarChart, Trash2, Layers, Globe } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных вариантов валютной ипотеки
 */
const ComparisonSidebar = ({ 
  savedLoans, 
  deleteSavedLoan, 
  loadSavedLoan,
  currencyRates
}) => {
  // Если нет сохраненных кредитов
  if (!savedLoans || savedLoans.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение вариантов ипотеки
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты валютной ипотеки для сравнения.
        </p>
      </div>
    );
  }

  // Получение символа валюты
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'CHF': return 'Fr';
      case 'JPY': case 'CNY': return '¥';
      default: return currencyCode;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение ипотеки
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedLoans.length} {savedLoans.length === 1 ? 'вариант' : savedLoans.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedLoans.map((loan) => (
          <div 
            key={loan.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Globe className="w-4 h-4 text-indigo-500 mr-1.5" />
                {loan.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedLoan(loan.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedLoan(loan.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Сумма:</span>
                <span className="font-medium">
                  {getCurrencySymbol(loan.currency)}{formatNumber(loan.formData.loanAmount, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Срок:</span>
                <span>
                  {loan.formData.loanTerm} {pluralize(parseFloat(loan.formData.loanTerm), 'год', 'года', 'лет')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Ставка:</span>
                <span>
                  {formatNumber(loan.formData.interestRate, 2)}%
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Платеж ({loan.currency}):</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {getCurrencySymbol(loan.currency)}{formatNumber(loan.results.averageMonthlyPayment, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Платеж (₽):</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(loan.results.firstMonthlyPaymentRUB, 0)} ₽
                </span>
              </div>
            </div>
            
            {/* Риск-индикатор */}
            {loan.riskAnalysis && (
              <div className={`mt-2 px-2 py-1 text-xs rounded-md text-center ${
                loan.riskAnalysis.currencyRiskLevel === 'low' 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : loan.riskAnalysis.currencyRiskLevel === 'medium'
                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {loan.riskAnalysis.currencyRiskLevel === 'low' 
                  ? 'Низкий уровень риска'
                  : loan.riskAnalysis.currencyRiskLevel === 'medium'
                    ? 'Средний уровень риска'
                    : 'Высокий уровень риска'
                }
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;